'use client';

import { useEffect, useEffectEvent } from 'react';

import type { Messages } from '@/shared-ui';

import {
  reportHandsFreeActivity,
  scheduleHandsFreeVoiceEntry,
} from './project-response-shared-effects.helpers';
import { runHandsFreePause } from './project-usability-handsfree-controls.helpers';

type ChatMessage = Messages[number];

export function useHandsFreeActivityTracking({
  isRecording,
  isListening,
  isSpeaking,
  updateActivity,
}: {
  isRecording: boolean;
  isListening: boolean;
  isSpeaking: boolean;
  updateActivity: () => void;
}): void {
  useEffect(
    () =>
      reportHandsFreeActivity({
        isRecording,
        isListening,
        isSpeaking,
        updateActivity,
      }),
    [isRecording, isListening, isSpeaking, updateActivity]
  );

  useEffect(() => {
    if (!isRecording || (!isListening && !isSpeaking)) return;

    const interval = setInterval(() => {
      if (isListening || isSpeaking) {
        updateActivity();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [isRecording, isListening, isSpeaking, updateActivity]);
}

export function useHandsFreeResponseMode({
  isRecording,
  isOnboardingActive,
  responseMode,
  setResponseMode,
}: {
  isRecording: boolean;
  isOnboardingActive: boolean;
  responseMode: string;
  setResponseMode: (mode: 'voice' | 'text') => void;
}): void {
  const setResponseModeEvent = useEffectEvent(setResponseMode);

  const shouldEnter =
    isRecording && !isOnboardingActive && responseMode !== 'voice';

  // Keyed on shouldEnter only: setResponseMode comes from an uncompiled context
  // provider whose identity changes every render, and each effect re-run resets
  // the voice-entry timer — under render churn (widget UT resume) the timer
  // would never fire and hands-free stayed stuck in text mode.
  useEffect(
    () =>
      scheduleHandsFreeVoiceEntry({
        shouldEnter,
        setResponseMode: (mode) => setResponseModeEvent(mode),
      }),
    [shouldEnter]
  );
}

export function useHandsFreeRecordingLifecycle({
  stopRecording,
}: {
  stopRecording: () => void;
}): void {
  useEffect(() => {
    const handleUnload = () => {
      stopRecording();
    };
    window.addEventListener('unload', handleUnload);
    return () => {
      stopRecording();
      window.removeEventListener('unload', handleUnload);
    };
  }, [stopRecording]);
}

interface UseHandsFreePlayPauseOptions {
  isRecording: boolean;
  isCurrentlyPaused: boolean;
  isAiVoiceEnabled: boolean;
  responseMode: 'voice' | 'text';
  isConversationOver: boolean;
  updateActivity: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopListening: (force?: boolean) => Promise<void>;
  startListening: () => Promise<void>;
  setIsUserPaused: (value: boolean) => void;
  resetAudioRecord: () => void;
  stopAudioPlayback: () => void;
  hidePopup: () => void;
  playOrEnqueueAudio: (options: {
    messageId: string;
    messageText: string;
    audioUrl: string;
    status: 'ready';
    shouldInterrupt: boolean;
  }) => void;
  lastAssistantMessage: ChatMessage | undefined;
  replyFeedbackMessage: ChatMessage | undefined;
}

interface UseHandsFreePlayPauseReturn {
  showPlayIcon: boolean;
  handlePauseRecording: () => void;
  handleResumeUserPause: () => void;
}

export function useHandsFreePlayPause({
  isRecording,
  isCurrentlyPaused,
  isAiVoiceEnabled,
  responseMode,
  isConversationOver,
  updateActivity,
  pauseRecording,
  resumeRecording,
  stopListening,
  startListening,
  setIsUserPaused,
  resetAudioRecord,
  stopAudioPlayback,
  hidePopup,
  playOrEnqueueAudio,
  lastAssistantMessage,
  replyFeedbackMessage,
}: UseHandsFreePlayPauseOptions): UseHandsFreePlayPauseReturn {
  const showPlayIcon = !isRecording || isCurrentlyPaused;

  const handlePauseRecording = () => {
    runHandsFreePause(isCurrentlyPaused, {
      pauseRecording,
      stopListening,
      setIsUserPaused,
      stopAudioPlayback,
      resetAudioRecord,
      hidePopup,
    });
  };

  const handleResumeUserPause = () => {
    if (!isCurrentlyPaused) return;
    resumeRecording();
    setIsUserPaused(false);
    updateActivity();
    stopListening(true)
      .then(() => startListening())
      .catch(() => undefined);

    if (isAiVoiceEnabled && responseMode === 'voice' && !isConversationOver) {
      const messagesToReplay = [];
      if (replyFeedbackMessage) {
        messagesToReplay.push(replyFeedbackMessage);
      }
      if (lastAssistantMessage) {
        messagesToReplay.push(lastAssistantMessage);
      }

      messagesToReplay.forEach((m, idx) => {
        if (m.audio?.audioUrl) {
          playOrEnqueueAudio({
            messageId: m.id,
            messageText: m.transcript || m.text,
            audioUrl: m.audio.audioUrl,
            status: 'ready',
            shouldInterrupt: idx === 0,
          });
        }
      });
    }
  };

  return {
    showPlayIcon,
    handlePauseRecording,
    handleResumeUserPause,
  };
}
